import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Landmark, RefreshCw, ArrowLeft, CheckCircle2, XCircle,
  AlertTriangle, Webhook, Wallet, Banknote, ExternalLink, Radio, ShieldQuestion,
  KeyRound,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtBal = (rows) => {
  if (!rows || rows.length === 0) return '$0.00';
  return rows.map((r) => `${fmtMoney(r.amount)} ${r.currency.toUpperCase()}`).join(' · ');
};
const fmtTime = (iso) => {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PAYOUT_BADGE = {
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_transit: 'bg-sky-50 text-sky-700 border-sky-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

const Tile = ({ testid, label, value, icon: Icon, tone }) => (
  <div data-testid={testid} className="cn-card p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
        <p className="cn-display text-2xl text-cn-text mt-1.5 tabular-nums truncate">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon className="w-4 h-4 text-cn-text-muted" />
    <h2 className="font-display font-bold text-lg text-cn-text">{children}</h2>
  </div>
);

/**
 * Admin "Payments Ops" — one pane of glass for live money:
 *   GET  /api/admin/payments-ops/overview
 *   POST /api/admin/payments-ops/connect-check
 *   POST /api/admin/payments-ops/webhooks/reregister
 */
export default function AdminPaymentsOps() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [checkingConnect, setCheckingConnect] = useState(false);
  const [reregistering, setReregistering] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const [ovr, rdy] = await Promise.all([
        axios.get(`${API}/admin/payments-ops/overview`, authHeader),
        axios.get(`${API}/admin/stripe/readiness`, authHeader),
      ]);
      setData(ovr.data);
      setReadiness(rdy.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Payments Ops');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); }, [load, token]);

  const runConnectCheck = async () => {
    setCheckingConnect(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/payments-ops/connect-check`, {}, authHeader);
      if (r.enabled) toast.success('Stripe Connect is ACTIVE — clinician onboarding will work.');
      else toast.error(`Connect not active yet: ${r.error || 'unknown error'}`);
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connect check failed');
    } finally {
      setCheckingConnect(false);
    }
  };

  const reregisterWebhooks = async () => {
    if (!window.confirm('Re-register both Stripe webhook endpoints against the CURRENT domain? Old endpoints will be deleted and new signing secrets stored automatically.')) return;
    setReregistering(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/payments-ops/webhooks/reregister`, {}, authHeader);
      toast.success(`Re-registered ${r.registered.length} endpoints on ${r.base_url}`);
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-registration failed');
    } finally {
      setReregistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center" data-testid="payments-ops-loading">
        <Loader2 className="w-6 h-6 animate-spin text-cn-primary" />
      </div>
    );
  }

  const escrow = data?.escrow || {};
  const connect = data?.connect || {};
  const payouts = data?.payouts || {};
  const endpoints = data?.webhook_endpoints || [];
  const log = data?.webhook_log || {};

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="admin-payments-ops-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="inline-flex items-center gap-1 text-xs font-semibold text-cn-text-muted hover:text-cn-primary mb-3"
              data-testid="payments-ops-back-btn"
            >
              <ArrowLeft className="w-3 h-3" /> Admin dashboard
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-cn-primary text-white flex items-center justify-center">
                <Landmark className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="cn-display text-3xl text-cn-text">Payments Ops</h1>
                  {data?.livemode && (
                    <span className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider" data-testid="payments-ops-live-badge">
                      Live
                    </span>
                  )}
                </div>
                <p className="text-sm text-cn-text-muted">
                  Live Stripe balance, settlement health, payouts and webhook delivery — spot stuck money early.
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="cn-btn cn-btn-ghost !py-2 text-xs"
            data-testid="payments-ops-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {' '}Refresh
          </button>
        </div>

        {/* Balance + settlement tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Tile testid="ops-tile-available" label="Stripe available" value={fmtBal(data?.balance?.available)} icon={Wallet} tone="bg-emerald-100 text-emerald-600" />
          <Tile testid="ops-tile-pending" label="Stripe pending" value={fmtBal(data?.balance?.pending)} icon={Wallet} tone="bg-sky-100 text-sky-600" />
          <Tile testid="ops-tile-awaiting-verification" label={`Awaiting verification (${escrow.held_count || 0})`} value={fmtMoney(escrow.held_total)} icon={Banknote} tone="bg-amber-100 text-amber-600" />
          <Tile testid="ops-tile-failed-payouts" label="Failed payouts" value={payouts.failed_count || 0} icon={XCircle} tone={payouts.failed_count > 0 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'} />
        </div>
        {data?.balance_error && (
          <div className="cn-card p-4 mb-6 border-red-200 bg-red-50 text-sm text-red-700" data-testid="ops-balance-error">
            Stripe balance unavailable: {data.balance_error}
          </div>
        )}

        {/* Stuck money alert */}
        {(escrow.stuck_initiated_count || 0) > 0 && (
          <div className="cn-card p-4 mb-6 border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="ops-stuck-money-alert">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>{escrow.stuck_initiated_count} checkout{escrow.stuck_initiated_count > 1 ? 's' : ''} stuck in “initiated” for &gt;24h</strong>
              {' '}totaling {fmtMoney(escrow.stuck_initiated_total)} — the customer opened Stripe Checkout but the payment never completed or the webhook never landed.
            </div>
          </div>
        )}

        {/* ── Live-Mode Readiness (iter130) ────────────────────────
             Answers the question: "what still needs to happen for
             this environment to run REAL money through Stripe?"
             Each requirement is a discrete check with a stable id + a
             human-readable next step. Never displays any secret value
             — only presence booleans + last-4-char tails. */}
        {readiness && (
          <div className="cn-card p-5 mb-6" data-testid="ops-readiness-card">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-start gap-3">
                <KeyRound
                  className={`w-5 h-5 mt-0.5 ${
                    readiness.livemode_ready ? 'text-emerald-600' : 'text-amber-600'
                  }`}
                />
                <div>
                  <h3 className="font-semibold text-cn-text">
                    Stripe Live Activation Readiness
                    <span
                      className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                        readiness.mode === 'LIVE'
                          ? 'bg-red-600 text-white'
                          : readiness.mode === 'TEST'
                            ? 'bg-sky-100 text-sky-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}
                      data-testid="ops-readiness-mode-badge"
                    >
                      {readiness.mode}
                    </span>
                  </h3>
                  <p className="text-sm text-cn-text-muted mt-0.5">
                    {readiness.next_action}
                  </p>
                </div>
              </div>
              {readiness.blockers && readiness.blockers.length > 0 && (
                <span className="text-xs px-2 py-1 rounded-full border bg-amber-50 border-amber-200 text-amber-700">
                  {readiness.blockers.length} blocker{readiness.blockers.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <ol className="space-y-2">
              {(readiness.checks || []).map((c) => (
                <li
                  key={c.id}
                  className="flex items-start gap-3 text-sm"
                  data-testid={`ops-readiness-check-${c.id}`}
                >
                  {c.pass ? (
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                  ) : c.optional ? (
                    <ShieldQuestion className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={c.pass ? 'text-cn-text' : 'font-semibold text-cn-text'}>
                        {c.label}
                      </span>
                      {c.optional && !c.pass && (
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">
                          optional
                        </span>
                      )}
                      {c.tail && (
                        <code className="text-[11px] text-cn-text-muted bg-slate-100 rounded px-1.5 py-0.5">
                          {c.tail}
                        </code>
                      )}
                      {c.value && (
                        <code className="text-[11px] text-cn-text-muted bg-slate-100 rounded px-1.5 py-0.5 truncate max-w-md">
                          {c.value}
                        </code>
                      )}
                    </div>
                    {!c.pass && (
                      <p className="text-xs text-cn-text-muted mt-1 leading-snug">
                        {c.detail}
                        {c.error && (
                          <>
                            {' '}
                            <span className="text-red-700">{c.error}</span>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Connect status */}
        <div className="cn-card p-5 mb-6" data-testid="ops-connect-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              {connect.enabled === true ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              ) : connect.enabled === false ? (
                <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
              ) : (
                <ShieldQuestion className="w-5 h-5 text-slate-400 mt-0.5" />
              )}
              <div>
                <h3 className="font-display font-bold text-base text-cn-text">
                  Stripe Connect{' '}
                  {connect.enabled === true ? 'active' : connect.enabled === false ? 'not activated' : '— status unknown'}
                </h3>
                <p className="text-sm text-cn-text-muted mt-0.5 max-w-xl">
                  {connect.enabled === true
                    ? `Clinician onboarding and real payouts are enabled. ${connect.accounts || 0} connected account(s) on file.`
                    : connect.enabled === false
                      ? (connect.error || 'Activate Connect in your Stripe dashboard to enable clinician onboarding and payouts.')
                      : 'Run a check to verify whether Connect is activated on this Stripe account.'}
                </p>
                {connect.checked_at && (
                  <p className="text-[11px] text-cn-text-muted mt-1">Last checked {fmtTime(connect.checked_at)}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {connect.enabled !== true && (
                <a
                  href="https://dashboard.stripe.com/connect"
                  target="_blank"
                  rel="noreferrer"
                  className="cn-btn cn-btn-ghost !py-2 text-xs"
                  data-testid="ops-connect-dashboard-link"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Activate in Stripe
                </a>
              )}
              <button
                onClick={runConnectCheck}
                disabled={checkingConnect}
                className="cn-btn cn-btn-primary !py-2 text-xs"
                data-testid="ops-connect-check-btn"
              >
                {checkingConnect ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radio className="w-3.5 h-3.5" />}
                {' '}Run Connect check
              </button>
            </div>
          </div>
        </div>

        {/* Webhook health */}
        <div className="cn-card p-5 mb-6" data-testid="ops-webhooks-card">
          <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
            <SectionTitle icon={Webhook}>Webhook health</SectionTitle>
            <button
              onClick={reregisterWebhooks}
              disabled={reregistering}
              className="cn-btn cn-btn-ghost !py-2 text-xs"
              data-testid="ops-reregister-webhooks-btn"
            >
              {reregistering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {' '}Re-register on current domain
            </button>
          </div>
          <p className="text-xs text-cn-text-muted mb-4">
            Current domain: <code className="text-[11px]">{data?.base_url}</code> — re-register after deploying to production so Stripe events reach the new URL (signing secrets update automatically).
          </p>
          {data?.webhook_endpoints_error ? (
            <p className="text-sm text-red-700" data-testid="ops-webhook-endpoints-error">{data.webhook_endpoints_error}</p>
          ) : endpoints.length === 0 ? (
            <p className="text-sm text-cn-text-muted" data-testid="ops-webhook-endpoints-empty">
              No webhook endpoints registered on Stripe — click “Re-register” to create them.
            </p>
          ) : (
            <ul className="space-y-2 mb-5" data-testid="ops-webhook-endpoints-list">
              {endpoints.map((ep) => (
                <li key={ep.id} className="flex items-center gap-3 text-sm border border-cn-border rounded-xl px-4 py-2.5" data-testid={`ops-webhook-endpoint-${ep.id}`}>
                  {ep.matches_domain
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    : <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />}
                  <span className="font-mono text-xs text-cn-text truncate flex-1">{ep.url}</span>
                  <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase ${ep.status === 'enabled' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {ep.status}
                  </span>
                  {!ep.matches_domain && (
                    <span className="text-[10px] font-semibold text-amber-700">stale domain</span>
                  )}
                  <span className="text-[11px] text-cn-text-muted">{ep.events} events</span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Recent deliveries received</p>
            {(log.failed_verifications || 0) > 0 && (
              <span className="text-[11px] font-semibold text-red-700" data-testid="ops-failed-verifications">
                {log.failed_verifications} rejected signature{log.failed_verifications > 1 ? 's' : ''} (forgery attempts blocked)
              </span>
            )}
          </div>
          {(log.recent || []).length === 0 ? (
            <p className="text-sm text-cn-text-muted" data-testid="ops-webhook-log-empty">No deliveries received yet.</p>
          ) : (
            <ul className="divide-y divide-cn-border" data-testid="ops-webhook-log-list">
              {log.recent.map((row) => (
                <li key={row.id} className="flex items-center gap-3 py-2 text-sm">
                  {row.verified
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    : <XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />}
                  <span className="font-mono text-xs text-cn-text">{row.event_type}</span>
                  <span className="text-[11px] text-cn-text-muted">{row.endpoint}</span>
                  <span className="text-[11px] text-cn-text-muted ml-auto">{fmtTime(row.received_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Payouts */}
        <div className="cn-card p-5" data-testid="ops-payouts-card">
          <SectionTitle icon={Banknote}>Recent clinician payouts</SectionTitle>
          {(payouts.recent || []).length === 0 ? (
            <p className="text-sm text-cn-text-muted" data-testid="ops-payouts-empty">No payouts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ops-payouts-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-cn-text-muted border-b border-cn-border">
                    <th className="py-2 pr-4 font-bold">Date</th>
                    <th className="py-2 pr-4 font-bold">Bank</th>
                    <th className="py-2 pr-4 font-bold">Net</th>
                    <th className="py-2 pr-4 font-bold">Method</th>
                    <th className="py-2 pr-4 font-bold">Status</th>
                    <th className="py-2 font-bold">Rail</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.recent.map((p) => (
                    <tr key={p.id} className={`border-b border-cn-border last:border-0 ${p.status === 'failed' ? 'bg-red-50' : ''}`} data-testid={`ops-payout-row-${p.id}`}>
                      <td className="py-2.5 pr-4 text-cn-text-muted whitespace-nowrap">{fmtTime(p.created_at)}</td>
                      <td className="py-2.5 pr-4 text-cn-text">{p.bank_name ? `${p.bank_name} ••${p.account_last4 || ''}` : '—'}</td>
                      <td className="py-2.5 pr-4 text-cn-text tabular-nums font-semibold">{fmtMoney(p.net)}</td>
                      <td className="py-2.5 pr-4 text-cn-text-muted capitalize">{p.method}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase ${PAYOUT_BADGE[p.status] || PAYOUT_BADGE.pending}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="py-2.5 text-[11px] text-cn-text-muted">{p.simulated ? 'simulated' : 'stripe'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
